import React from 'react';
import { Box, Typography } from '@mui/material';
import { NAVY, eyebrowSx, motion } from './shellTokens';

interface RailSectionLabelProps {
  label: string;
  expanded: boolean;
  reducedMotion: boolean;
}

/**
 * Section eyebrow ("BUSINESS MODULES").
 *
 * Collapsed, the words can't fit at 76px, so the label degrades to a short
 * centred hairline — the grouping is still visible as rhythm, and no text is
 * clipped into nonsense. The element is a real <li> because it lives inside the
 * nav's <ul>; a bare div there is invalid markup and screen readers skip it.
 *
 * No extra CSS `opacity` on the text: the muted token is already
 * semi-transparent, and layering opacity on top compounds the two and drops
 * 10px text below AA. The eyebrow is distinguished by the mono face, size and
 * tracking instead.
 */
const RailSectionLabel: React.FC<RailSectionLabelProps> = ({ label, expanded, reducedMotion }) => (
  <Box component="li" sx={{ listStyle: 'none', px: 1.75, pt: 2.25, pb: 1 }}>
    {expanded ? (
      <Typography
        sx={{
          ...eyebrowSx,
          color: NAVY.textMuted,
          whiteSpace: 'nowrap',
          transition: motion(reducedMotion, 'opacity .16s ease'),
        }}
      >
        {label}
      </Typography>
    ) : (
      <>
        <Box
          aria-hidden="true"
          sx={{ height: 1, width: 24, mx: 'auto', backgroundColor: NAVY.borderStrong }}
        />
        {/* The hairline carries no meaning to a screen reader, so the label
            itself stays in the accessibility tree, visually clipped. */}
        <Box
          component="span"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
      </>
    )}
  </Box>
);

export default RailSectionLabel;
